// services/quickEntryService.ts
import { supabase } from '@/integrations/supabase/client';
import { unifiedLibraryService } from './unifiedLibraryService';

interface ServiceResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

class QuickEntryService {
  private static instance: QuickEntryService;

  private constructor() {}

  public static getInstance(): QuickEntryService {
    if (!QuickEntryService.instance) {
      QuickEntryService.instance = new QuickEntryService();
    }
    return QuickEntryService.instance;
  }

  private async getCurrentUser(): Promise<{ id: string }> {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user) {
      throw new Error('User not authenticated');
    }
    return user;
  }

  // ==================== ESG METRICS ====================
  async createEsgMetric(metricData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      // Try to find matching metric in library
      const { data: libraryMetrics, error: libraryError } = await supabase
        .from('unified_metrics_library')
        .select('id, name, category, unit, currency')
        .ilike('name', `%${metricData.name}%`)
        .eq('category', metricData.category)
        .limit(1);

      if (libraryError) throw libraryError;

      if (libraryMetrics?.[0]?.id) {
        // Use existing library metric
        const result = await unifiedLibraryService.createMetricFromLibrary(
          libraryMetrics[0].id,
          {
            custom_value: parseFloat(metricData.value) || 0,
            custom_metadata: {
              reporting_period: metricData.reporting_period,
              data_source: metricData.data_source || 'Manual Entry',
              scope: metricData.scope,
              notes: metricData.notes
            }
          }
        );
        
        return { success: true, data: result };
      }

      // Create custom metric
      const { data: metric, error } = await supabase
        .from('user_metrics_data')
        .insert({
          organization_id: user.id,
          name: metricData.name,
          category: metricData.category,
          subcategory: metricData.subcategory,
          description: metricData.description,
          unit: metricData.unit,
          currency: metricData.currency || 'USD',
          custom_value: parseFloat(metricData.value) || 0,
          custom_metadata: {
            reporting_period: metricData.reporting_period,
            data_source: metricData.data_source || 'Manual Entry',
            scope: metricData.scope,
            notes: metricData.notes
          },
          is_custom: true
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: metric };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create ESG metric'
      };
    }
  }

  // ==================== MATERIALS ====================
  async createMaterial(materialData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      // Try to find matching material in library
      const { data: libraryMaterials, error: libraryError } = await supabase
        .from('unified_materials_library')
        .select('id, name, category, unit, currency')
        .ilike('name', `%${materialData.name}%`)
        .eq('category', materialData.category)
        .limit(1);

      if (libraryError) throw libraryError;

      if (libraryMaterials?.[0]?.id) {
        // Use existing library material
        const result = await unifiedLibraryService.createMaterialFromLibrary(
          libraryMaterials[0].id,
          {
            custom_density: parseFloat(materialData.density) || undefined,
            custom_carbon_footprint: parseFloat(materialData.carbon_footprint) || undefined,
            custom_cost_per_unit: parseFloat(materialData.cost_per_unit) || undefined,
            custom_metadata: {
              reporting_period: materialData.reporting_period,
              data_source: materialData.data_source || 'Manual Entry',
              scope: materialData.scope,
              notes: materialData.notes
            }
          }
        );
        
        return { success: true, data: result };
      }

      // Create custom material
      const { data: material, error } = await supabase
        .from('user_materials_data')
        .insert({
          organization_id: user.id,
          name: materialData.name,
          category: materialData.category,
          subcategory: materialData.subcategory,
          description: materialData.description,
          unit: materialData.unit,
          density: parseFloat(materialData.density) || null,
          carbon_footprint: parseFloat(materialData.carbon_footprint) || null,
          cost_per_unit: parseFloat(materialData.cost_per_unit) || null,
          currency: materialData.currency || 'USD',
          custom_metadata: {
            reporting_period: materialData.reporting_period,
            data_source: materialData.data_source || 'Manual Entry',
            scope: materialData.scope,
            notes: materialData.notes
          },
          is_custom: true
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: material };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create material'
      };
    }
  }

  // ==================== RISK ASSESSMENTS ====================
  async createRiskAssessment(riskData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: risk, error } = await supabase
        .from('risk_assessments')
        .insert({
          user_id: user.id,
          risk_category: riskData.risk_category,
          risk_name: riskData.risk_name,
          risk_description: riskData.risk_description,
          likelihood: riskData.likelihood,
          impact: riskData.impact,
          mitigation_strategy: riskData.mitigation_strategy,
          responsible_person: riskData.responsible_person,
          review_date: riskData.review_date || null,
          status: riskData.status || 'active',
          notes: riskData.notes
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: risk };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create risk assessment'
      };
    }
  }

  // ==================== INVESTMENTS ====================
  async createInvestment(investmentData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: investment, error } = await supabase
        .from('portfolio_investments')
        .insert({
          user_id: user.id,
          investment_name: investmentData.investment_name,
          investment_type: investmentData.investment_type,
          sector: investmentData.sector,
          region: investmentData.region,
          investment_amount: parseFloat(investmentData.investment_amount) || 0,
          currency: investmentData.currency || 'USD',
          esg_score: investmentData.esg_score ? parseFloat(investmentData.esg_score) : null,
          environmental_score: investmentData.environmental_score ? parseFloat(investmentData.environmental_score) : null,
          social_score: investmentData.social_score ? parseFloat(investmentData.social_score) : null,
          governance_score: investmentData.governance_score ? parseFloat(investmentData.governance_score) : null,
          carbon_intensity: investmentData.carbon_intensity ? parseFloat(investmentData.carbon_intensity) : null,
          water_intensity: investmentData.water_intensity ? parseFloat(investmentData.water_intensity) : null,
          waste_intensity: investmentData.waste_intensity ? parseFloat(investmentData.waste_intensity) : null,
          investment_date: investmentData.investment_date,
          last_reviewed: investmentData.last_reviewed || null,
          status: investmentData.status || 'active',
          notes: investmentData.notes
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: investment };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create investment'
      };
    }
  }

  // ==================== INITIATIVES ====================
  async createInitiative(initiativeData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: initiative, error } = await supabase
        .from('decarbonization_initiatives')
        .insert({
          user_id: user.id,
          target_id: initiativeData.target_id || null,
          initiative_name: initiativeData.initiative_name,
          initiative_type: initiativeData.initiative_type,
          description: initiativeData.description,
          estimated_reduction: parseFloat(initiativeData.estimated_reduction) || 0,
          investment_required: initiativeData.investment_required ? parseFloat(initiativeData.investment_required) : null,
          currency: initiativeData.currency || 'USD',
          implementation_date: initiativeData.implementation_date,
          completion_date: initiativeData.completion_date || null,
          status: initiativeData.status || 'planned',
          actual_reduction: initiativeData.actual_reduction ? parseFloat(initiativeData.actual_reduction) : null,
          roi_percentage: initiativeData.roi_percentage ? parseFloat(initiativeData.roi_percentage) : null,
          responsible_team: initiativeData.responsible_team,
          priority_level: initiativeData.priority_level || 'medium',
          notes: initiativeData.notes,
          outcome_summary: initiativeData.outcome_summary || null
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: initiative };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create initiative'
      };
    }
  }

  // ==================== SUPPLIERS ====================
  async createSupplier(supplierData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: supplier, error } = await supabase
        .from('suppliers')
        .insert({
          user_id: user.id,
          supplier_name: supplierData.supplier_name,
          supplier_type: supplierData.supplier_type,
          industry_sector: supplierData.industry_sector,
          country: supplierData.country,
          region: supplierData.region,
          contact_email: supplierData.contact_email,
          contact_phone: supplierData.contact_phone,
          annual_spend: supplierData.annual_spend ? parseFloat(supplierData.annual_spend) : null,
          currency: supplierData.currency || 'USD',
          esg_assessment_score: supplierData.esg_assessment_score ? parseFloat(supplierData.esg_assessment_score) : null,
          carbon_footprint: supplierData.carbon_footprint ? parseFloat(supplierData.carbon_footprint) : null,
          water_usage: supplierData.water_usage ? parseFloat(supplierData.water_usage) : null,
          waste_generation: supplierData.waste_generation ? parseFloat(supplierData.waste_generation) : null,
          labor_practices_score: supplierData.labor_practices_score ? parseFloat(supplierData.labor_practices_score) : null,
          environmental_compliance: Boolean(supplierData.environmental_compliance),
          social_compliance: Boolean(supplierData.social_compliance),
          governance_compliance: Boolean(supplierData.governance_compliance),
          certification_standards: supplierData.certification_standards,
          risk_level: supplierData.risk_level || 'medium',
          last_assessment_date: supplierData.last_assessment_date || null,
          next_assessment_date: supplierData.next_assessment_date || null,
          status: supplierData.status || 'active',
          notes: supplierData.notes
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: supplier };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create supplier'
      };
    }
  }

  // ==================== ASSESSMENTS ====================
  async createAssessment(assessmentData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: assessment, error } = await supabase
        .from('supply_chain_assessments')
        .insert({
          user_id: user.id,
          supplier_id: assessmentData.supplier_id || null,
          assessment_type: assessmentData.assessment_type,
          assessment_date: assessmentData.assessment_date,
          assessor_name: assessmentData.assessor_name || null,
          overall_score: assessmentData.overall_score ? parseFloat(assessmentData.overall_score) : null,
          environmental_score: assessmentData.environmental_score ? parseFloat(assessmentData.environmental_score) : null,
          social_score: assessmentData.social_score ? parseFloat(assessmentData.social_score) : null,
          governance_score: assessmentData.governance_score ? parseFloat(assessmentData.governance_score) : null,
          findings: assessmentData.findings || null,
          recommendations: assessmentData.recommendations || null,
          action_items: assessmentData.action_items || null,
          follow_up_date: assessmentData.follow_up_date || null,
          status: assessmentData.status || 'pending',
          notes: assessmentData.notes || null,
          
          // New fields
          has_ems: Boolean(assessmentData.has_ems),
          policies_environmental: Boolean(assessmentData.policies_environmental),
          policies_human_rights: Boolean(assessmentData.policies_human_rights),
          policies_anti_corruption: Boolean(assessmentData.policies_anti_corruption),
          data_privacy_policy: Boolean(assessmentData.data_privacy_policy),
          whistleblower_mechanism: Boolean(assessmentData.whistleblower_mechanism),
          sbti_committed: Boolean(assessmentData.sbti_committed),
          ghg_disclosed: Boolean(assessmentData.ghg_disclosed),
          ecovadis_score: assessmentData.ecovadis_score ? parseFloat(assessmentData.ecovadis_score) : null,
          cdp_score: assessmentData.cdp_score || null,
          scope1_emissions: assessmentData.scope1_emissions ? parseFloat(assessmentData.scope1_emissions) : null,
          scope2_emissions: assessmentData.scope2_emissions ? parseFloat(assessmentData.scope2_emissions) : null,
          scope3_emissions: assessmentData.scope3_emissions ? parseFloat(assessmentData.scope3_emissions) : null,
          energy_consumption_mwh: assessmentData.energy_consumption_mwh ? parseFloat(assessmentData.energy_consumption_mwh) : null,
          water_withdrawal_m3: assessmentData.water_withdrawal_m3 ? parseFloat(assessmentData.water_withdrawal_m3) : null,
          waste_generated_tons: assessmentData.waste_generated_tons ? parseFloat(assessmentData.waste_generated_tons) : null,
          incidents_count: assessmentData.incidents_count ? parseInt(assessmentData.incidents_count) : null,
          incidents_details: assessmentData.incidents_details || null,
          cap_in_place: Boolean(assessmentData.cap_in_place),
          cap_due_date: assessmentData.cap_due_date || null,
          custom_fields: assessmentData.custom_fields || []
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: assessment };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create assessment'
      };
    }
  }

  // ==================== SUPPLIERS (GET) ====================
  async getSuppliers(): Promise<ServiceResponse<any[]>> {
    try {
      const user = await this.getCurrentUser();

      const { data: suppliers, error } = await supabase
        .from('suppliers')
        .select('id, supplier_name, esg_assessment_score, status')
        .eq('user_id', user.id)
        .order('supplier_name');

      if (error) throw error;
      return { success: true, data: suppliers || [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get suppliers'
      };
    }
  }

  // ==================== TEMPLATE MANAGEMENT ====================
  async getAssessmentTemplates(): Promise<ServiceResponse<any[]>> {
    try {
      const user = await this.getCurrentUser();

      const { data: templates, error } = await supabase
        .from('assessment_templates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        // Table might not exist yet - return empty array
        return { success: true, data: [] };
      }

      return { success: true, data: templates || [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get assessment templates'
      };
    }
  }

  async saveAssessmentTemplate(name: string, templateData: any): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: template, error } = await supabase
        .from('assessment_templates')
        .insert({
          user_id: user.id,
          name,
          template_data: templateData
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: template };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save assessment template'
      };
    }
  }

  async applyAssessmentTemplate(templateId: string): Promise<ServiceResponse> {
    try {
      const user = await this.getCurrentUser();

      const { data: template, error } = await supabase
        .from('assessment_templates')
        .select('template_data')
        .eq('id', templateId)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return { success: true, data: template?.template_data || {} };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to apply assessment template'
      };
    }
  }

  // ==================== CONNECTION TESTING ====================
  async testConnection(): Promise<ServiceResponse> {
    try {
      const tables = [
        'risk_assessments',
        'portfolio_investments',
        'decarbonization_initiatives',
        'suppliers',
        'supply_chain_assessments',
        'assessment_templates'
      ];

      const results: Record<string, boolean> = {};
      
      for (const table of tables) {
        const { error } = await supabase
          .from(table)
          .select('id')
          .limit(1);
        
        results[table] = !error;
      }

      return {
        success: true,
        data: {
          tables: results,
          message: 'Connection test completed'
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to test database connection'
      };
    }
  }
}

export const quickEntryService = QuickEntryService.getInstance();